import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';

const DemoEmailModal = ({ open, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);

    try {
      const { demoService } = await import('../services/demoService');
      const demoData = await demoService.loginDemo(email);
      // Pass both the API response and the prospect's actual email
      onSuccess(demoData, email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setError(null);
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isValidEmail(email) && !loading) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          backgroundColor: 'rgb(212, 82, 33)',
          color: 'white',
          mx: -1,
          mt: -1,
          px: 3,
          pt: 2,
          borderRadius: '12px 12px 0 0'
        }}
      >
        <Typography
          variant="h4"
          component="div"
          sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontWeight: 'normal',
            color: 'white'
          }}
        >
          TRY DEMO MODE
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your email to start a 45-minute free demo session. Any data you enter or changes you make will not be stored. Enter a valid e-mail address to start.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            autoFocus
            error={email.length > 0 && !isValidEmail(email)}
            helperText={
              email.length > 0 && !isValidEmail(email)
                ? 'Please enter a valid email address'
                : ''
            }
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#021c2d'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#021c2d'
              }
            }}
          />

          <Box
            sx={{
              backgroundColor: '#f5f7fb',
              borderRadius: 2,
              p: 2,
              mb: 2,
              borderLeft: '4px solid #FFD700'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#021c2d' }}>
              Demo Session Includes:
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              • 45-minute session with read-only access
              <br />
              • AI Evaluator agentic interview (answers not saved)
              <br />
              • Full access to reports, scorecards, and analytics
              <br />
              • Explore organization and user management
              <br />
              • Pre-populated account with 15+ employee interviews
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#2f2f2f',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(47, 47, 47, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValidEmail(email) || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            backgroundColor: '#2f2f2f',
            color: 'white',
            textTransform: 'none',
            py: 1.5,
            px: 3,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#404040'
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(47, 47, 47, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          {loading ? 'Starting Demo...' : 'Start Demo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemoEmailModal;
